import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { toast } from "sonner";

export type GuidanceKind = "out_of_scope" | "not_relevant" | "walker_error";

export const KIND_LABEL: Record<GuidanceKind, string> = {
  out_of_scope: "Out of scope",
  not_relevant: "Not relevant",
  walker_error: "Runner error",
};

export interface GuidanceRule {
  id: string;
  kind: string;
  rule: string;
  reason: string | null;
  scope: { capabilityId?: string; outlineId?: string };
  sourceCanonicalIds: string[];
  status: string;
  createdAt: string | Date;
}

export function AddGuidanceRuleDialog({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const [rule, setRule] = useState("");
  const [reason, setReason] = useState("");
  const [kind, setKind] = useState<GuidanceKind>("out_of_scope");

  const create = trpc.guidance.create.useMutation({
    onSuccess: () => {
      utils.guidance.list.invalidate({ projectId });
      toast.success("Runner rule added");
      onClose();
    },
    onError: (err) => toastTrpcError(err, "Failed to add runner rule"),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add runner rule</DialogTitle>
          <DialogDescription>
            Teach Iamu what is out of scope, not relevant, or misread by the
            runner. Walkthrough runners follow this rule on future runs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="create-rule">Rule instruction</Label>
            <Textarea
              id="create-rule"
              placeholder="e.g. 24-hour serious incident reporting is handled out-of-band by phone; do not flag missing in-app submission."
              value={rule}
              onChange={(e) => setRule(e.target.value)}
              rows={3}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-reason">Rationale (optional)</Label>
            <Textarea
              id="create-reason"
              placeholder="e.g. Handled manually by safety officers to adhere to Swedish law."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-kind">Classification</Label>
            <Select
              value={kind}
              onValueChange={(v) => setKind(v as GuidanceKind)}
            >
              <SelectTrigger id="create-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="out_of_scope">
                  {KIND_LABEL.out_of_scope}
                </SelectItem>
                <SelectItem value="not_relevant">
                  {KIND_LABEL.not_relevant}
                </SelectItem>
                <SelectItem value="walker_error">
                  {KIND_LABEL.walker_error}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!rule.trim() || create.isPending}
            onClick={() =>
              create.mutate({
                projectId,
                rule: rule.trim(),
                reason: reason.trim() || null,
                kind,
              })
            }
          >
            {create.isPending ? "Adding..." : "Add rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditGuidanceRuleDialog({
  projectId,
  rule,
  onClose,
}: {
  projectId: string;
  rule: GuidanceRule;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const [text, setText] = useState(rule.rule);
  const [reason, setReason] = useState(rule.reason ?? "");
  const [kind, setKind] = useState<GuidanceKind>(rule.kind as GuidanceKind);

  const update = trpc.guidance.update.useMutation({
    onSuccess: () => {
      utils.guidance.list.invalidate({ projectId });
      toast.success("Runner rule updated");
      onClose();
    },
    onError: (err) => toastTrpcError(err, "Failed to update runner rule"),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit runner rule</DialogTitle>
          <DialogDescription>
            Update how walkthrough runners should follow this negative rule.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="edit-rule">Rule instruction</Label>
            <Textarea
              id="edit-rule"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-reason">Rationale (optional)</Label>
            <Textarea
              id="edit-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-kind">Classification</Label>
            <Select
              value={kind}
              onValueChange={(v) => setKind(v as GuidanceKind)}
            >
              <SelectTrigger id="edit-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="out_of_scope">
                  {KIND_LABEL.out_of_scope}
                </SelectItem>
                <SelectItem value="not_relevant">
                  {KIND_LABEL.not_relevant}
                </SelectItem>
                <SelectItem value="walker_error">
                  {KIND_LABEL.walker_error}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!text.trim() || update.isPending}
            onClick={() =>
              update.mutate({
                projectId,
                id: rule.id,
                rule: text.trim(),
                reason: reason.trim() || null,
                kind,
              })
            }
          >
            {update.isPending ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
